<template>
    <v-form>
        <v-card>
            <v-toolbar dark color="primary">
                <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
                <slot name="header"></slot>
            </v-toolbar>
            <v-card-text>
              <v-container grid-list-md>
                <v-layout wrap>
                    <v-flex xs12>
                        <v-text-field
                            v-model="value.description"
                            label="What is the description?"
                            placeholder="e.g. Fuel pump, Hydraulic pump etc"
                            v-validate="'required'"
                            :error-messages="errors.first('description')"
                            name="description"
                            maxlength="150"
                            required
                        ></v-text-field>
                    </v-flex>
                    <v-flex xs12>
                        <v-autocomplete
                            label="EDN Number"
                            item-value="id"
                            item-text="ednNumber"
                            :items="defectNotices"
                          cache-items
                            :value-comparator="compareDefectNoticeId"
                            no-filter
                            :loading="isSearchingDefectNotice"
                            :search-input.sync="search"
                            v-model="value.defectNoticeId"
                            no-data-text="Start typing an EDN number"
                            :error-messages="errors.collect('defect notice')"
                            name="defect notice"
                            autocomplete="off"
                        >
                            <template v-slot:item="{ item }">
                                <v-list-item-content>
                                    <v-list-item-title>{{ item.ednNumber }}</v-list-item-title>
                                </v-list-item-content>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                    <v-flex xs12>
                        <v-menu
                            v-model="startDateDialog"
                            max-width="290px"
                            :close-on-content-click="false"
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    :value="formattedStartDate"
                                    label="When does the warranty start?"
                                    readonly
                                    v-validate="'required'"
                                    :error-messages="errors.collect('warranty start')"
                                    name="warranty start"
                                    v-on="on"
                                    required
                                    append-icon="event"
                                    @click:append="startDateDialog = true"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="value.startDate"
                                @change="startDateDialog = false"
                                no-title
                            ></v-date-picker>
                        </v-menu>
                    </v-flex>
                    <v-flex xs6>
                        <v-text-field
                            v-model="value.length"
                            label="The warranty length"
                            v-validate="'required|min_value:1'"
                            :error-messages="errors.collect('warranty length')"
                            name="warranty length"
                            type="number"
                            min="1"
                            required
                        ></v-text-field>
                    </v-flex>
                    <v-flex xs6>
                        <v-select
                            label="months / years"
                            item-value="key"
                            item-text="value"
                            :items="intervalTypes"
                            v-model="value.warrantyInterval"
                            v-validate="'required'"
                            :error-messages="errors.collect('warranty interval')"
                            name="warranty interval"
                            required
                        ></v-select>
                    </v-flex>
                </v-layout>
            </v-container>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <slot name="actions"></slot>
                <v-btn @click="close">Cancel</v-btn>
                <v-btn
                    color="primary"
                    :disabled="disableSubmit"
                    @click="submit"
                >{{ !this.editing ? 'Save Changes' : 'Update' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>
  import FormValidation from '@/components/FormValidation.vue';
  import {WarrantyIntervalType} from '@/models/enums';
  import moment from 'moment';
  import {DateFormat} from '@/plugins/date-filter';
  import DefectNotices from '@/components/DefectNotices/DefectNoticesClient.vue';

  export default {
    name: 'CustomWarrantyForm',
    props: {
      value: {
        required: true
      },
      garageLocationId: {
        type: String,
        required: true
      }
    },
    mixins: [FormValidation, DefectNotices],
    computed: {
      formTitle() {
        return !this.editing ? 'Add Additional Warranty' : 'Edit Additional Warranty';
      },
      editing() {
        return !!this.value.id;
      },
      formattedStartDate() {
        return this.value.startDate ? moment(this.value.startDate).format(DateFormat.DefaultDisplay) : '';
      }
    },
    methods: {
      async getDefectNotices() {
        let defectNotices = {};
        try {
          this.$wait.start('loading pending defects');
          const response = await this.$api.get(`units/${this.filterParams.unitId}/pending-defects`);
          const data = response.data;
          defectNotices = {...data};
        } catch (error) {
          this.$toast.error('There was an error getting pending defects');
        } finally {
          this.$wait.end('loading pending defects');
        }
        try {
          this.$wait.start('loading defects');
          const response = await this.$api.get(`defects?unitId=${this.filterParams.unitId}`);
          const data = response.data;
          defectNotices = {...defectNotices, defects: data.data};
        } catch (error) {
          this.$toast.error('There was an error getting defects');
        } finally {
          this.$wait.end('loading defects');
        }
        return defectNotices;
      },
      async resetForm(ednNumber) {
        this.id = '';
        this.lastSearchTerm = '';
        this.defectNotices = [];
        this.search = ednNumber;
        this.submitted = false;
      },
      submitForm() {
        this.submitted = true;
        this.addOrUpdate();
      },
      addOrUpdate() {
        if (!this.editing) {
          this.$emit('add', {id: this.id, model: this.value});
        } else {
          this.$emit('edit', {id: this.value.id, model: this.value});
        }
      },
      close() {
        this.$emit('close');
      },
      compareDefectNoticeId(a, b) {
        return (a == null && b == null) || (a != null && b != null && a.toString() === b.toString());
      },
      seedSelectedDefectNotice(model) {
        if (!(model && model.defectNoticeId && model.ednNumber)) {
          return;
        }

        const selectedDefectNotice = {
          id: model.defectNoticeId.toString(),
          ednNumber: model.ednNumber
        };

        this.defectNotices = [selectedDefectNotice]
          .concat(this.defectNotices.filter(x => x.id !== selectedDefectNotice.id));
      },
      performSearch: _.debounce(async (val, me) => {
          const searchValue = (val || (me.value.defectNoticeId ? me.value.ednNumber : '') || '').trim();
          if (!searchValue) {
            me.lastSearchTerm = '';
            if (!me.value.defectNoticeId) {
              me.defectNotices = [];
            }
                return;
            }

          if (me.lastSearchTerm === searchValue && me.defectNotices.length > 0) {
            return;
          }

          me.lastSearchTerm = searchValue;

            try {
                me.isSearchingDefectNotice = true;
                const defectData = await me.getDefectNotices();
            const defects = defectData.defects || [];
            const noticesRequiringActioning = defectData.noticesRequiringActioning || [];

            const defectNotices = defects.map(x => ({id: x.defectNoticeId, ednNumber: x.ednNumber}))
              .concat(noticesRequiringActioning.map(x => ({id: x.id, ednNumber: x.ednNumber})))
              .filter(x => x.id && x.ednNumber)
              .map(x => ({id: x.id.toString(), ednNumber: x.ednNumber}));

            const matchingEdnNumber = defectNotices.filter(x => x.ednNumber.toLowerCase().includes(searchValue.toLowerCase()));
                me.defectNotices = matchingEdnNumber;
            } catch (error) {
                me.$toast.error('There was an error searching for defect notices');
            }
            me.isSearchingDefectNotice = false;
        }, 200)
    },
    data() {
      return {
        id: '',
        submitted: false,
        startDateDialog: false,
        isSearchingDefectNotice: false,
        search: null,
        lastSearchTerm: '',
        filterParams: {
          unitId: this.value.unitId,
          garageLocationId: this.garageLocationId
        },
        defectNotices: [],
        openOptions: {
          sortBy: ['receivedDate'],
          sortDescending: false,
          itemsPerPage: 500
        },
        completedOptions: {
          sortBy: ['receivedDate'],
          sortDescending: true,
          itemsPerPage: 500
        },
        intervalTypes: [
          {
            key: WarrantyIntervalType.Months,
            value: 'Months'
          },
          {
            key: WarrantyIntervalType.Years,
            value: 'Years'
          }
        ],
      };
    },
    watch: {
      value(val) {
        if (val.id) {
          this.id = val.id;
        } else {
          this.id = this.$uuid.v1();
        }

        this.seedSelectedDefectNotice(val);

        this.filterParams.unitId = val.unitId;
        this.lastSearchTerm = '';

        if (!this.value.loaded) {
          this.value.loaded = true;
        }

        this.submitted = false;
      },
      async search(val) {
        this.performSearch(val, this);
      }
    },
    async mounted() {
        this.seedSelectedDefectNotice(this.value);

        this.search = this.value.ednNumber || null;
        await this.performSearch(this.search, this);
    }  };
</script>
